import { createSelector } from '@reduxjs/toolkit';
import {
  getAllGroups,
  getGroupIdProps,
  getGroupsByWorkspaceIdSelector,
  getWorkspaceIdProps,
} from '../../../../../redux/groups/selectors';
import { ChannelMap, Group } from '../../../../../redux/groups/api/group.model';
import { ComposableDeviceUserInterfaces } from '../../../../../redux/composableDevice/ComposableDevice.model';
import { Merchandiser } from '../model/Merchandiser.model';
import _ from 'lodash';
import { getMachinesTypeSelector } from '../../../../../redux/composableDevice/selectors/composableDeviceSelectors';
import { MachineType } from '../../../../../redux/stoerkID/StoerkIdAPI.model';

import { GroupAPI } from '../../../../../redux/groups/api/GroupAPI';
import { BusinessRole } from '../../businessModel/model/model';
import { RootState } from '../../../../../redux/store.model';

/**
 * List of supported composable device types
 *
 * Those ids comes from the real ids that Structural Concepts uses for their devices
 * So are areal family/group of devices that we support, at the momento Oasis merchandisers
 */
const listTypeSupported = [
  ComposableDeviceUserInterfaces.SCC_MERCHANDISE,
  ComposableDeviceUserInterfaces.SCC_MERCHANDISE_V2,
];

const getRefrigeratedShelfs = (group: Group) => {
  const composableDeviceData = group?.composableDevice;
  if (composableDeviceData?.labels?.cus_refrigerated?.trim() === '') {
    return [];
  }

  return (
    (composableDeviceData?.labels?.cus_refrigerated?.split(',') || []).map(
      (shelf: string) => {
        const [commanderId, controlUnitId] = shelf.split('/');
        return {
          commanderId,
          controlUnitId,
        };
      }
    ) ?? []
  );
};
const getMappedChannels = (group: Group, machineType?: MachineType) => {
  if (!machineType) {
    return [];
  }
  const mappedChannelsValues: any = [];
  const composableDeviceData = group?.composableDevice?.channels ?? [];
  if (machineType?.labels) {
    composableDeviceData
      .filter((data) => !!data.controlUnitId)
      .forEach((channel: ChannelMap, index: number) => {
        mappedChannelsValues.push({
          name: machineType.labels[`channels.${index}.name`],
          ...channel,
        });
      });
  }

  return mappedChannelsValues;
};

const getHeatedShelfs = (group: Group) => {
  const composableDeviceData = group?.composableDevice;
  if (composableDeviceData?.labels?.cus_heated?.trim() === '') {
    return [];
  }

  return (
    (composableDeviceData?.labels?.cus_heated?.split(',') || []).map(
      (shelf: string) => {
        const [commanderId, controlUnitId] = shelf.split('/');
        return {
          commanderId,
          controlUnitId,
        };
      }
    ) ?? []
  );
};

const getGroupTreeSelected = createSelector(
  [getGroupIdProps, getAllGroups],
  (groupId, groups) => {
    if (!groupId) {
      return groups;
    }
    return groups.filter(
      (group) => group.id === groupId || group.pathGroupsIds.includes(groupId)
    );
  }
);

export const getMachinesTypeIdSupportedSelector = createSelector(
  [getMachinesTypeSelector],
  (machinesType) => {
    return machinesType
      .filter((machineType) =>
        listTypeSupported.includes(
          machineType.userInterface as ComposableDeviceUserInterfaces
        )
      )
      .map((machineType) => machineType.id);
  }
);

export const getMerchandisersOnStoreOnMyWorkspace = createSelector(
  [
    getWorkspaceIdProps,
    getGroupIdProps,
    (state: RootState, props: { workspaceId: string; groupId: string }) => {
      return getGroupsByWorkspaceIdSelector(state, props);
    },
  ],
  (_workspaceId, _groupId, workspaceGroupsResponse) => {
    const groupsEntities = workspaceGroupsResponse.data?.entities.groups;
    if (!groupsEntities) {
      return [];
    }

    // Extract all merchandisers (child groups of STORE groups)
    // Using flatMap and sorting by ID for stable array references
    const merchandisers = Object.values(groupsEntities)
      .filter(
        (group): group is Group =>
          !!group && group.attributes?.BUSINESS_ROLE === BusinessRole.STORE
      )
      .flatMap((storeGroup) => {
        if (!storeGroup.childGroups || !Array.isArray(storeGroup.childGroups)) {
          return [];
        }
        return storeGroup.childGroups
          .map((childGroupId) => groupsEntities[childGroupId])
          .filter((childGroup): childGroup is Group => !!childGroup);
      })
      .sort((a, b) => a.id.localeCompare(b.id)); // Sort by ID for consistent ordering

    return merchandisers;
  }
);

export const getAllComposableDeviceByPropGroupIdSelector = createSelector(
  [
    getGroupIdProps,
    getWorkspaceIdProps,
    getGroupTreeSelected,
    getMachinesTypeIdSupportedSelector,
    getMachinesTypeSelector,
    getMerchandisersOnStoreOnMyWorkspace,
  ],
  (
    groupId,
    workspaceId,
    groups,
    machineTypeIdSupported,
    machinesType,
    stores
  ) => {
    // Combine groups and stores, removing duplicates by ID
    const uniqueGroups = _.uniqBy([...groups, ...stores], 'id');

    return uniqueGroups
      .filter((group: Group | undefined) => {
        return (
          groupId === group?.parentGroupId &&
          group?.composableDevice &&
          machineTypeIdSupported.includes(
            group?.composableDevice?.machineTypeId ?? ''
          )
        );
      })
      .map((group) => {
        const machineType = machinesType.find(
          (machineType) =>
            machineType.id === group?.composableDevice?.machineTypeId
        );
        const refrigeratedShelfs = getRefrigeratedShelfs(group);
        const heatedShelfs = getHeatedShelfs(group);
        const channels = getMappedChannels(group, machineType);
        return {
          ...group,
          // Merchandiser only with one device
          deviceId: _.first(group.devices),
          machineType,
          refrigeratedShelfs,
          heatedShelfs,
          channels,
        } as Merchandiser;
      });
  }
);

export const getMerchandiserByMerchandiserIdPropSelector = createSelector(
  [
    (_state, props: { merchandiserId?: string }) => props.merchandiserId,
    getAllComposableDeviceByPropGroupIdSelector,
  ],
  (merchandiserId, merchandisers) => {
    return merchandisers.find(
      (merchandiser) => merchandiser.id === merchandiserId
    );
  }
);
